import { ApolloQueryResult } from '@apollo/client/core';
import { Injectable, NgZone, inject } from '@angular/core';
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { Apollo } from "apollo-angular";
import { Router } from "@angular/router";
import { MatSnackBar } from '@angular/material/snack-bar';
import { GET_USER_INFO } from "@riise/graphql/users/users.queries";
import { Observable, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { CryptoService } from '@riise/core/services/crypto/crypto.service';
import { TranslateService } from '@ngx-translate/core';



@Injectable({
    providedIn: 'root'
})
export class AuthService {

    private firebaseAuth = inject(AngularFireAuth);
    private apollo = inject(Apollo);
    private router = inject(Router);
    private ngZone = inject(NgZone);
    private snackBar = inject(MatSnackBar);
    private crypto = inject(CryptoService);
    private translateService = inject(TranslateService);


    currentUser$: Observable<any> | any;
    //espaces autorisés
    spaces: any[] = ["2", "3", "4", "7"];


    constructor() {
        this.currentUser$ = this.initCurrentUser();
    }

    private initCurrentUser(): Observable<any | null> {
        return this.firebaseAuth.authState.pipe(
            switchMap(firebaseUser => {
                if (firebaseUser) {
                    return this.getUserData(firebaseUser.uid).pipe(
                        map(userData => ({
                            ...userData,
                            emailVerified: firebaseUser.emailVerified
                        }))
                    );
                } else {
                    return of(null);
                }
            }),
            catchError(error => {
                this.handleError(error);
                return of(null);
            })
        );
    }

    private getUserData(userId: string) {
        return this.apollo.watchQuery<any>({
            query: GET_USER_INFO,
            fetchPolicy: 'no-cache',
            variables: { id: userId }
        }).valueChanges.pipe(
            map(result => this.mapUserData(result?.data?.userById, userId))
        );
    }

    private mapUserData(userData: any, userId: string): any {
        const groupedReferences = this.groupBySpaceWithRoles(userData?.flagUser);
        return {
            uid: userId,
            email: userData?.email,
            firstName: userData?.firstName,
            lastName: userData?.lastName,
            fullName: `${userData?.firstName} ${userData?.lastName}`,
            username: userData?.email,
            isAdmin: userData?.isAdmin,
            flags: groupedReferences
        };
    }

    setUserData(): void {
        this.currentUser$.subscribe((user: any) => {
          const validIndex = user?.flags?.findIndex((space: any) => this.spaces.includes(space?.id)) ?? -1;

          if (validIndex !== -1) {
              let encryptedUserData = this.crypto.encryptData(user);
              localStorage.setItem('user', JSON.stringify(encryptedUserData));
              localStorage.setItem('index', String(validIndex));
              this.ngZone.run(() => {
                this.router.navigate(['/dashboard']).then(() => {window.location.reload()});
              });
          } else {
            // pop up message to the user that he is not authorized to access the application
            this.snackBar.open(this.translateService.instant("Vous n'êtes pas autorisé à accéder à cette application"), this.translateService.instant("Fermer"), {
              duration: 5000,
              verticalPosition: 'top',
              horizontalPosition: 'center',
              panelClass: ['error-snackbar']
            });
          }
        });
    }

    get isLoggedIn(): boolean {
        const user = JSON.parse(localStorage.getItem('user')!);
        const decryptedUserData = this.crypto.decryptData(user)
        return decryptedUserData !== null
    }


    get isUserLoggedIn(): boolean {
        const user = JSON.parse(localStorage.getItem('user')!);
        const decryptedUserData = this.crypto.decryptData(user)
        return decryptedUserData !== null;
    }



    signOut(): Promise<void> {
        return this.firebaseAuth.signOut().then(() => {
            localStorage.removeItem('user');
            this.ngZone.run(() => {
                this.router.navigate(['auth/']);
            });
        });
    }

    private handleError(error: any): void {
        this.snackBar.open(this.translateService.instant("Une erreur est survenue"), this.translateService.instant("Fermer"), {
            duration: 5000,
            verticalPosition: 'top',
            horizontalPosition: 'center',
            panelClass: ['error-snackbar']
        });
    }

    groupBySpaceWithRoles = (data: any) => {
        if (!data || !data?.edges) {
          return [];
        }

        const groupedData: any[] = [];

        // Parcours des éléments de data.edges
        for (const edge of data?.edges) {
          const flagSpaceReference = edge?.node?.flagSpaceReference;
          const flagsspaceusersrolesSet = edge?.node?.flagsspaceusersrolesSet;

          if (flagSpaceReference?.flagSpace?.space) {
            const spaceName = flagSpaceReference?.flagSpace?.space?.name;
            const spaceId = flagSpaceReference?.flagSpace?.space?.id;
            const reference = flagSpaceReference?.reference;
            const flagsName = flagSpaceReference?.flagSpace?.flag?.name;

            if (flagsspaceusersrolesSet?.edges) {
              for (const roleEdge of flagsspaceusersrolesSet.edges) {
                if (roleEdge?.node?.flagSpaceRole) {
                  const roleName = roleEdge?.node?.flagSpaceRole?.name;
                  const roleId = roleEdge?.node?.flagSpaceRole?.id;

                  // Recherche si l'espace est déjà dans groupedData
                  let spaceEntry = groupedData.find(entry => entry?.name === spaceName);

                  if (!spaceEntry) {
                    // Si l'espace n'existe pas encore, l'initialiser
                    spaceEntry = {
                      name: spaceName,
                      id: spaceId,
                      roles: []
                    };
                    groupedData.push(spaceEntry);
                  }

                  // Recherche si le rôle est déjà présent pour cet espace par roleName uniquement
                  let roleEntry = spaceEntry.roles.find((role: any) => role?.roleName === roleName);

                  if (roleEntry) {
                    // Si le rôle existe déjà, concaténer les nouvelles références et roleIds
                    roleEntry.reference = roleEntry.reference
                      ? `${roleEntry.reference}, ${reference}`
                      : reference;
                    roleEntry.roleId = roleEntry.roleId
                      ? `${roleEntry.roleId}, ${roleId}`
                      : roleId;
                  } else {
                    // Si le rôle n'existe pas encore, ajouter un nouvel objet
                    spaceEntry.roles.push({
                      roleName: roleName,
                      roleId: roleId, // Conserver le roleId associé
                      flagsName: flagsName,
                      reference: reference
                    });
                  }
                }
              }
            }
          }
        }

        // Consolidation des références et roleIds pour les rôles ayant les mêmes noms
        groupedData.forEach(space => {
          const roleMap = new Map<string, any>();

          space.roles.forEach((role: any) => {
            const key = role.roleName;
            if (roleMap.has(key)) {
              // Si le rôle existe déjà dans la map, concaténer les références et roleIds
              const existingRole = roleMap.get(key);
              existingRole.reference = existingRole?.reference
                ? `${existingRole.reference}, ${role?.reference}`
                : role?.reference;
              existingRole.roleId = existingRole?.roleId
                ? `${existingRole?.roleId}, ${role?.roleId}`
                : role?.roleId;
            } else {
              // Ajouter le rôle à la map
              roleMap.set(key, { ...role });
            }
          });

          // Convertir la map en tableau pour les rôles consolidés
          space.roles = Array.from(roleMap.values());
        });
        return groupedData;
    };









}
